import {
    AtlasAttachmentLoader,
    SkeletonJson,
    SpineTexture,
    TextureAtlas,
    Spine,
    type BoneData,
    type Slot,
    type SlotData,
} from '@esotericsoftware/spine-pixi-v8';
import type {
    SpineID,
    SpineLayoutOptions,
    SpineInstanceData,
    TextsJson,
} from './config/types';
import { type AssetsManifest, BitmapText, Container, Text, Texture, type Point } from 'pixi.js';
import { LOG } from './config/logs';
import { log } from './utils/Log';
import { ManifestParser, type SpineAssetData } from './utils/ManifestParser';
import { planMultipleInstances } from './utils/multiInstance';
import { AnimationsController } from './controllers/Animations.controller';
import { SkinsController } from './controllers/Skins.controller';
import { TextsController } from './controllers/Texts.controller';
import { SceneController } from './controllers/Scene.controller';
import { SpineController } from './controllers/Spine.controller';
import { sounds } from './controllers/Sounds.controller';

export class SpineLayout extends Container {
    #spines: Map<SpineID, Spine> = new Map();
    /** Ids of spines created as multiple instances (e.g. `counter_1`). */
    #multipleInstanceIDs: Set<string> = new Set();
    #animations: AnimationsController;
    #skins: SkinsController;
    #texts: TextsController;
    #spine: SpineController;
    #scene: SceneController;

    constructor(private options?: SpineLayoutOptions) {
        super();

        log.enabled = !!options?.debug;
        this.#animations = new AnimationsController(this.#spines);
        this.#skins = new SkinsController(this.#spines);
        this.#texts = new TextsController(this.#spines, this.#multipleInstanceIDs);
        this.#spine = new SpineController(this.#spines, this.#animations);
        this.#scene = new SceneController(
            this.#spines,
            this.#texts,
            this.#animations,
            this.#spine,
            options,
        );

        if (options?.manifest) {
            this.createInstancesFromManifest(options.manifest);
        }
    }

    // ─── setters / getters ───────────────────────────────────────────────────────

    get spines(): Map<SpineID, Spine> {
        return this.#spines;
    }
    /** Ids of spines created as multiple instances (e.g. `counter_1`, `counter_2`). */
    get multipleInstanceIds(): string[] {
        return [...this.#multipleInstanceIDs];
    }
    get animations(): AnimationsController {
        return this.#animations;
    }
    get skins(): SkinsController {
        return this.#skins;
    }
    get scene(): SceneController {
        return this.#scene;
    }
    get texts(): TextsController {
        return this.#texts;
    }
    get spine(): SpineController {
        return this.#spine;
    }

    set textSettings(settings: TextsJson) {
        this.#texts.settings = settings;
    }
    get textSettings(): TextsJson | undefined {
        return this.#texts.settings;
    }

    // TODO: UI
    // get ui(): PixiUI {}

    set debug(value: boolean) {
        if (this.options) this.options.debug = value;
        log.enabled = value;
    }

    get debug(): boolean {
        return log.enabled;
    }

    set speed(value: number) {
        this.#animations.speed = value;
    }
    get speed(): number {
        return this.#animations.speed;
    }

    // ─── Instance creation ───────────────────────────────────────────────────────

    createInstancesFromManifest(manifest: AssetsManifest, folderName?: string) {
        log.open(LOG.SPINES);
        log.open(LOG.STATES);
        log.open(LOG.EVENTS);
        log.open(LOG.SPINE_EVENTS);

        // Create a single instance for every asset first, so that parent spines exist
        // and their slots can be scanned for multiple-instance pointers.
        const assetByID = new Map<string, SpineAssetData>();
        ManifestParser.getSpineAssets(manifest).forEach((asset) => {
            const spineID = asset.atlas.replace(/\.[^.]+$/, '');
            assetByID.set(spineID, asset);
            try {
                this.addSpineInstance(spineID, this.spineFromAsset(asset, folderName));
            } catch (e) {
                console.error(`[SpineLayout] Error loading spine "${spineID}":`, e);
            }
        });

        this.multiplyInstances(
            (baseID) => assetByID.has(baseID),
            (baseID, instanceID) => {
                try {
                    this.addSpineInstance(
                        instanceID,
                        this.spineFromAsset(assetByID.get(baseID)!, folderName),
                    );
                } catch (e) {
                    console.error(`[SpineLayout] Error loading spine instance "${instanceID}":`, e);
                }
            },
        );

        this.render();

        sounds.init(manifest);

        this.#animations.playState('init');
        this.#animations.playByName('init');

        log.close(LOG.SPINES);
        log.close(LOG.STATES);
        log.close(LOG.EVENTS);
        log.close(LOG.SPINE_EVENTS);
    }

    createInstancesFromDataArray(data: SpineInstanceData[]) {
        log.open(LOG.SPINES);
        log.open(LOG.STATES);
        log.open(LOG.EVENTS);
        log.open(LOG.SPINE_EVENTS);

        // Create a single instance per data item first, so that parent spines exist
        // and their slots can be scanned for multiple-instance pointers.
        const dataByID = new Map<string, SpineInstanceData>();
        data.forEach((item) => {
            const spineID = item.name;
            dataByID.set(spineID, item);
            try {
                this.addDataInstance(spineID, item);
            } catch (e) {
                console.error(`[SpineLayout] Error loading spine "${spineID}":`, e);
            }
        });

        this.multiplyInstances(
            (baseID) => dataByID.has(baseID),
            (baseID, instanceID) => {
                try {
                    this.addDataInstance(instanceID, dataByID.get(baseID)!);
                } catch (e) {
                    console.error(`[SpineLayout] Error loading spine instance "${instanceID}":`, e);
                }
            },
        );

        this.render();

        log.close(LOG.SPINES);
        log.close(LOG.STATES);
        log.close(LOG.EVENTS);
        log.close(LOG.SPINE_EVENTS);

        this.#animations.playState('init');
        this.#animations.playByName('init');
    }

    /** Builds a Spine from raw data, registers it under `spineID`, and registers its skins. */
    private addDataInstance(spineID: string, data: SpineInstanceData) {
        const spineAtlas = new TextureAtlas(data.atlasText);

        for (const page of spineAtlas.pages) {
            const texture = data.textures[page.name];
            if (!texture) throw new Error(`Missing texture for page: ${page.name}`);
            page.setTexture(SpineTexture.from(texture.source));
        }

        const skeletonData = new SkeletonJson(
            new AtlasAttachmentLoader(spineAtlas),
        ).readSkeletonData(data.skeleton);
        const spine = new Spine(skeletonData);

        this.addSpineInstance(spineID, spine);

        data.skeleton.skins.forEach((skin) => {
            const defaultSkin =
                spine.skeleton.data.findSkin('default') ?? spine.skeleton.data.findSkin('basic');
            if (defaultSkin) this.#skins.applyBySpineID(spineID, defaultSkin.name);
            this.#skins.registerSkin(spineID, skin.name);
        });
    }

    /** Creates a Spine instance from a manifest asset entry. */
    private spineFromAsset(asset: SpineAssetData, folderName?: string): Spine {
        return Spine.from({
            skeleton: `${folderName}/${asset.skel}`,
            atlas: `${folderName}/${asset.atlas}`,
        });
    }

    /**
     * Expands base spines that act as multiple-instance templates into their full instance
     * set (see {@link planMultipleInstances} for the supported pointer conventions). Each
     * planned base is removed and replaced by its instances, which are tracked in
     * {@link #multipleInstanceIDs} and built via `spawn`.
     *
     * @param hasSource whether a base id has a backing asset/data entry to clone from.
     * @param spawn     creates and registers one instance for `(baseID, instanceID)`.
     */
    private multiplyInstances(
        hasSource: (baseID: string) => boolean,
        spawn: (baseID: string, instanceID: string) => void,
    ) {
        const bases = [...this.#spines.entries()].map(([id, spine]) => ({
            id,
            slots: spine.state.data.skeletonData.slots.map((slot) => slot.name),
        }));

        planMultipleInstances(bases).forEach(({ baseID, instanceIDs }) => {
            if (!hasSource(baseID)) return;

            this.removeSpineInstance(baseID);
            instanceIDs.forEach((instanceID) => {
                this.#multipleInstanceIDs.add(instanceID);
                spawn(baseID, instanceID);
            });
        });
    }

    private addSpineInstance(spineID: string, spine: Spine) {
        if (this.#spines.has(spineID)) {
            this.#spines.get(spineID)?.destroy();
            this.#spines.delete(spineID);
        }

        this.#spines.set(spineID, spine);
        this.#animations.registerSpine(spineID, spine);
        this.onSpineRegistered(spineID, spine);
    }

    private removeSpineInstance(spineID: string) {
        const spine = this.#spines.get(spineID);
        if (!spine) return;

        spine.destroy();
        this.#spines.delete(spineID);
        this.#animations.unregisterSpine(spineID);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected onSpineRegistered(_spineID: string, _spine: Spine) { }

    private render() {
        this.#texts.loadSettings();
        this.#scene.attachBones((spine) => this.addChild(spine));
        this.#scene.attachTexts();
        this.#scene.activateButtonBones();
        this.#scene.syncSlotObjectsWithDrawOrder();
    }

    // ─── Lifecycle ───────────────────────────────────────────────────────────────

    reset() {
        this.#texts.clear();
        this.#animations.clear();
        this.#skins.clear();
        this.#scene.clear();

        this.#spines.forEach((spine) => spine.destroy());
        this.#spines.clear();
        this.#multipleInstanceIDs.clear();

        this.removeChildren();
    }

    destroy() {
        this.reset();
        super.destroy();
    }

    // ─── deprecated methods ──────────────────────────────────────────────────────

    // ─── Spine registry ──────────────────────────────────────────────────────────

    getSpines(): Map<SpineID, Spine> {
        return this.#spines;
    }
    getSpine(spineID: string): Spine | undefined {
        return this.#spines.get(spineID);
    }
    getSpineByID(spineID: string): Spine | undefined {
        return this.#spines.get(spineID);
    }
    getSpinesByNamePattern(pattern: string, options?: { not?: string[] }): Map<string, Spine> {
        return this.#spine.getSpinesByNamePattern(pattern, options);
    }

    // ─── Animation queries ───────────────────────────────────────────────────────

    /** @deprecated Use `animations.getAll()` instead. */
    getAnimations(): string[] {
        return this.#animations.getAll();
    }
    /** @deprecated Use `animations.getStates()` instead. */
    getAnimationsStates(): string[] {
        return this.#animations.getStates();
    }
    /** @deprecated Use `animations.getEvents()` instead. */
    getAnimationsEvents(): string[] {
        return this.#animations.getEvents();
    }
    /** @deprecated Use `animations.getActive()` instead. */
    getActiveAnimations(): string[] {
        return this.#animations.getActive();
    }
    /** @deprecated Use `animations.getLooping()` instead. */
    getLoopingAnimations(): string[] {
        return this.#animations.getLooping();
    }

    // ─── Animation playback ──────────────────────────────────────────────────────

    /** @deprecated Use `animations.playState()` instead. */
    async playState(stateName: string) {
        return this.#animations.playState(stateName);
    }
    /** @deprecated Use `animations.playEvent()` instead. */
    async playEvent(eventName: string, spineID: string) {
        return this.#animations.playEvent(eventName, spineID);
    }
    /** @deprecated Use `animations.playByName()` instead. */
    async playAnimationByName(animationName: string, playSolo = false, trackID?: number) {
        return this.#animations.playByName(animationName, playSolo, trackID);
    }
    /** @deprecated Use `animations.play()` instead. */
    async playInstanceAnimation(
        spineID: string,
        animation: string,
        playSolo = false,
        trackID?: number,
    ) {
        return this.#animations.play(spineID, animation, playSolo, trackID);
    }
    /** @deprecated Use `animations.playSolo()` instead. */
    async playSolo(animationName: string) {
        return this.#animations.playSolo(animationName);
    }
    /** @deprecated Use `animations.playInstanceAnimationLastFrame()` instead. */
    async playInstanceAnimationLastFrame(spineID: string, animation: string, playSolo = false) {
        return this.#animations.playLastFrame(spineID, animation, playSolo);
    }
    /** @deprecated Use `animations.stopAnimation()` instead. */
    stopAnimation(spineID: string, animation: string) {
        this.#animations.stop(spineID, animation);
    }
    /** @deprecated Use `animations.stopAll()` instead. */
    stopAll() {
        this.#animations.stopAll();
    }
    /** @deprecated Use `animations.stopAllBySpineID()` instead. */
    stopAllBySpineID(spineID: string) {
        this.#animations.stopAllForSpine(spineID);
    }
    /** @deprecated Use `animations.pauseState()` instead. */
    pauseState(stateName: string) {
        this.#animations.pauseState(stateName);
    }
    /** @deprecated Use `animations.stopState()` instead. */
    stopState(stateName: string, resetPose = true) {
        this.#animations.stopState(stateName, resetPose);
    }
    /** @deprecated Use `animations.pauseSpineByID()` instead. */
    pauseSpineByID(spineID: string) {
        this.#animations.pauseBySpineID(spineID);
    }
    /** @deprecated Use `animations.pauseAnimation()` instead. */
    pauseAnimation(spineID: string, animation: string) {
        this.#animations.pause(spineID, animation);
    }
    /** @deprecated Use `animations.resetAnimation()` instead. */
    resetAnimation(spineID: string, animation: string) {
        this.#animations.reset(spineID, animation);
    }
    /** @deprecated Use `animations.addEventListener()` instead. */
    addEventListener(event: string, fn: (event: unknown) => void) {
        this.#animations.addEventListener(event, fn);
    }

    // ─── Skin management ─────────────────────────────────────────────────────────

    /** @deprecated Use `skins.getAll()` instead. */
    getAllSkins(): Set<string> {
        return this.#skins.getAll();
    }
    /** @deprecated Use `skins.apply()` instead. */
    applySkin(skin: string) {
        this.#skins.apply(skin);
    }
    /** @deprecated Use `skins.applyBySpineID()` instead. */
    applySpineSkin(spineID: string, skinName: string) {
        this.#skins.applyBySpineID(spineID, skinName);
    }
    /** @deprecated Use `skins.getSpineSkinsBySkinID()` instead. */
    getSpineSkins(skinID: string): string[] {
        return this.#skins.getSpineSkinsBySkinID(skinID);
    }

    // ─── Text management ─────────────────────────────────────────────────────────

    /** @deprecated Use `texts.getInstances()` instead. */
    getTextInstances(): Map<SpineID, Text | BitmapText> {
        return this.#texts.getInstances();
    }
    /** @deprecated Use `texts.getBitmapInstances()` instead. */
    getBitmapTextInstances(): Map<SpineID, BitmapText> {
        return this.#texts.getBitmapInstances();
    }
    /** @deprecated Use `texts.getBySpine()` instead. */
    getTextsBySpine(): Map<string, string[]> {
        return this.#texts.getBySpine();
    }
    /** @deprecated Use `texts.getVal()` instead. */
    getTextVal(textID: string): string | undefined {
        return this.#texts.getVal(textID);
    }
    /** @deprecated Use `texts.set()` instead. */
    async setText(boneName: string, text: string, animate = false, duration = 0) {
        await this.#texts.set(boneName, text, animate, duration);
    }
    /** @deprecated Use `texts.setOffset()` instead. */
    setTextOffset(boneName: string, offset: { x: number; y: number }) {
        this.#texts.setOffset(boneName, offset);
    }
    /** @deprecated Use `texts.setMaxWidth()` instead. */
    setTextMaxWidth(boneName: string, maxWidth: number) {
        this.#texts.setMaxWidth(boneName, maxWidth);
    }
    /** @deprecated Use `texts.setStyle()` instead. */
    setTextStyle(boneName: string, style: Partial<Text['style']>) {
        this.#texts.setStyle(boneName, style);
    }
    /** @deprecated Use `texts.setBySpineID()` instead. */
    setSlotTextBySpineID(spineID: string, slotName: string, text: Text | BitmapText) {
        this.#texts.addTextToSlot(spineID, slotName, text);
    }

    // ─── Spine management ─────────────────────────────────────────────────────────

    /** @deprecated Use `SpineController.getBonesByNamePattern()` directly instead. */
    getBonesByNamePattern(pattern: string): BoneData[] {
        return this.#spine.getBonesByNamePattern(pattern);
    }
    /** @deprecated Use `SpineController.getSlotsByNamePattern()` directly instead. */
    getSlotsByNamePattern(pattern: string): SlotData[] {
        return this.#spine.getSlotsByNamePattern(pattern);
    }
    /** @deprecated Use `SpineController.getSlotByName()` directly instead. */
    getSlotByName(name: string): Slot | undefined {
        return this.#spine.getSlotByName(name);
    }
    /** @deprecated Use `SpineController.getBoneGlobalPosition()` directly instead. */
    getSpineBoneGlobalPosition(spineID: string, boneName: string): Point | undefined {
        return this.#spine.getBoneGlobalPosition(spineID, boneName);
    }
    /** @deprecated Use `SpineController.getBonesGlobalPositionsByNamePattern()` directly instead. */
    getBonesGlobalPositionsByNamePattern(pattern: string): Record<string, Point> {
        return this.#spine.getBonesGlobalPositionsByNamePattern(pattern);
    }
    /** @deprecated Use `SpineController.getSlotsGlobalPositionsByNamePattern()` directly instead. */
    getSlotsGlobalPositionsByNamePattern(pattern: string): Record<string, Point> {
        return this.#spine.getSlotsGlobalPositionsByNamePattern(pattern);
    }
    /** @deprecated Use `SpineController.getSlotTexture()` directly instead. */
    getSpineSlotTexture(spineName: string, slotName: string): Texture | null {
        return this.#spine.getSlotTexture(spineName, slotName);
    }

    // ─── Manual slot/child wiring ────────────────────────────────────────────────

    /** @deprecated Use `scene.addSlotChild()` instead. */
    addSlotChild(spineID: string, slotName: string, child: Container) {
        this.#scene.addSlotChild(spineID, slotName, child);
    }

    /** @deprecated Use `spine.clone()` instead. */
    cloneSpine(spine: Spine, newSpineID: string): Spine {
        return this.#spine.clone(spine, newSpineID);
    }

    /** Removes attached children from a spine slot (or all slots if a container is given). */
    removeSlotAttachments(spineID: string, slotOrContainer: number | string | Slot | Container) {
        const spine = this.#spines.get(spineID);
        if (!spine) {
            console.error(`Spine ${spineID} not found`);
            return;
        }
        spine.removeSlotObject(slotOrContainer);
    }
}
